import { useEffect, useState } from 'react';
import { api, IS_NATIVE_APP } from '../api';
import { DEFAULT_BG_REMOVAL_CONFIG, type BgRemovalConfig } from './config';
import { isDeviceSupported } from './capability';
import { createBrowserRemover, type BackgroundRemover } from './client';

export interface BackgroundRemovalState {
  /** Flaggan är på för användaren och enheten klarar modellen. */
  available: boolean;
  config: BgRemovalConfig;
  remover: BackgroundRemover | null;
}

const OFF: BackgroundRemovalState = { available: false, config: DEFAULT_BG_REMOVAL_CONFIG, remover: null };

// Ett uppslag per inloggning och sidladdning — flaggan beror på användaren,
// enhetskontrollen (WebGPU) görs bara om flaggan är på.
let cached: { key: string; promise: Promise<BackgroundRemovalState> } | null = null;

function resolveState(): Promise<BackgroundRemovalState> {
  const key = api.authKey();
  if (cached?.key === key) return cached.promise;
  const promise = (async () => {
    // iOS-appens webbvy (WKWebView) har mycket mindre minne än Safari: att
    // ladda modellen (~90 MB) och köra den på WebGPU fick appen att låsa sig
    // hos testare (26 okt). Av i appen tills det är provat på riktiga telefoner.
    if (!key || IS_NATIVE_APP) return OFF;
    const { backgroundRemoval: config } = await api.getFeatures();
    if (!config.enabled || !(await isDeviceSupported(config.dtype))) return { ...OFF, config };
    // Fas 2: välj här en serverbaserad BackgroundRemover som reserv.
    return { available: true, config, remover: createBrowserRemover(config) };
  })().catch(() => OFF);
  cached = { key, promise };
  return promise;
}

export function useBackgroundRemoval(): BackgroundRemovalState {
  const [state, setState] = useState<BackgroundRemovalState>(OFF);
  useEffect(() => {
    let alive = true;
    resolveState().then(s => { if (alive) setState(s); });
    return () => { alive = false; };
  }, []);
  return state;
}
